import axios from 'axios';
import filterByTypeAndSubType from '../components/cdBuilder/utilities/filterByTypeAndSubType';
import { APIFactory } from '../ui-toolkit/rest/APIFactory';
import { baseUrlService } from '../ui-toolkit/rest/BaseUrlService';
import { ICDV3ViewItem } from '../ui-toolkit/cdItems/interfaces/ICDViewItem';
import { ICDV3ItemsResponse } from '../ui-toolkit/cdItems/interfaces/ICDV3ItemsResponse';
import { transformFromViewItemToServiceItem } from '../ui-toolkit/cdItems/service/transformFromViewItemToServiceItem';
import { transformFromServiceItemToViewItem } from '../ui-toolkit/cdItems/service/transformFromServiceItemToViewItem';
import { getCDItemsWithDetailsByOwnerId } from '../ui-toolkit/cdItems/service/ServiceMethods';
import { ICDV3ServiceItem } from '../ui-toolkit/cdItems/service/ServiceInterfaces';
import { eRecordingFees } from '../ui-toolkit/enums/eRecordingFees';
import { eCDV3ItemTypes } from '../ui-toolkit/enums/eCDV3ItemTypes';
import { Logger } from '../ui-toolkit/logging/LoggerService';

const isNoVal = (val: any) => val === undefined || val === null || val === '';
const filterByNonDeletableItems = filterByTypeAndSubType(
  eCDV3ItemTypes.percentage_of_loan_amount,
  [eCDV3ItemTypes.recording_fee, eRecordingFees.deed],
  [eCDV3ItemTypes.recording_fee, eRecordingFees.mortgage],
  eCDV3ItemTypes.lender_credit,
  eCDV3ItemTypes.personal_property_sale,
  eCDV3ItemTypes.buyer_reimbursement_to_seller,
  eCDV3ItemTypes.buyer_deposit,
  eCDV3ItemTypes.seller_excess_deposit,
  eCDV3ItemTypes.existing_loans_assumed,
  eCDV3ItemTypes.seller_credit,
  eCDV3ItemTypes.buyer_reimbursement_from_seller
);
const filterByEmptyFields = (
  item: ICDV3ViewItem & {
    date_from?: any;
    date_to?: any;
    payee?: any;
    percentage?: any;
  }
) =>
  [
    item.buyer_at_closing,
    item.buyer_before_closing,
    item.seller_at_closing,
    item.seller_before_closing,
    item.paid_by_others,
    item.percentage,
    item.date_from,
    item.date_to,
  ].reduce((isEmpty, val) => isNoVal(val) && isEmpty, true);

export const getCDItemsAsync = async (ownerId: string): Promise<ICDV3ItemsResponse> => {
  try {
    const data = await getCDItemsWithDetailsByOwnerId(ownerId);
    if (data instanceof Error) {
      throw data;
    } else {
      return data;
    }
  } catch (error: any) {
    Logger.error('Error in RestItemsService.get', { error, ownerId });
    throw new Error('There was an error while communicating with CD Items Service!');
  }
};

export const postCDItemsAsync = async (items: ICDV3ViewItem[]): Promise<ICDV3ViewItem[]> => {
  try {
    const response = await APIFactory(baseUrlService.ApiLocation).post<ICDV3ServiceItem[]>(
      `/cd_items/v3/items`,
      items.map(transformFromViewItemToServiceItem)
    );
    return response.data.map(transformFromServiceItemToViewItem);
  } catch (error: any) {
    Logger.error('Error in RestItemsService.post', { error, items });
    throw new Error('There was an error while communicating with CD Items Service!');
  }
};

export const putCDItemsAsync = async (items: ICDV3ViewItem[]): Promise<ICDV3ViewItem[]> => {
  try {
    const promises = items
      .map(transformFromViewItemToServiceItem)
      .map((item) =>
        APIFactory(baseUrlService.ApiLocation).put<ICDV3ServiceItem>(`/cd_items/v3/items/${item.id}`, item)
      );
    const all = await axios.all(promises);
    return all.map((response) => response.data).map(transformFromServiceItemToViewItem);
  } catch (error: any) {
    Logger.error('Error in RestItemsService.put', { error, items });
    throw new Error('There was an error while communicating with CD Items Service!');
  }
};

export const deleteCDItemsAsync = async (items: ICDV3ViewItem[]): Promise<ICDV3ViewItem[]> => {
  try {
    const promises = items.map((item) =>
      APIFactory(baseUrlService.ApiLocation).delete(`/cd_items/v3/items/${item.id}`)
    );
    const all = await axios.all(promises);
    return all.map((response) => response.data).map((status, index) => items[index]);
  } catch (error: any) {
    Logger.error('Error in RestItemsService.delete', { error, items });
    throw new Error('There was an error while communicating with CD Items Service!');
  }
};

export const saveCDItemsAsync = async (
  ownerId: string,
  items: ICDV3ViewItem[],
  deleteItems?: ICDV3ViewItem[]
): Promise<ICDV3ItemsResponse> => {
  const { added, deleted, updated } = items.reduce(
    (object, item) => {
      object[item.new ? 'added' : 'updated'].push(item);
      return object;
    },
    {
      added: [] as ICDV3ViewItem[],
      deleted: deleteItems && deleteItems.length ? deleteItems.filter((item) => !item.new) : [],
      updated: [] as ICDV3ViewItem[],
    }
  );

  // FYI: some items may have NULL amount in UI but not in API. Null amount translates to DELETE.
  const addedItemsWithNoAmount = added.filter(filterByNonDeletableItems).filter(filterByEmptyFields);
  const updatedItemsWithNoAmount = updated.filter(filterByNonDeletableItems).filter(filterByEmptyFields);
  // Logger.debug('items that should be deleted', {
  //   addedItemsWithNoAmount,
  //   updatedItemsWithNoAmount
  // });
  const set = {
    added: added.filter((item) => !addedItemsWithNoAmount.includes(item)),
    deleted: [...deleted, ...updatedItemsWithNoAmount],
    updated: updated.filter((item) => !updatedItemsWithNoAmount.includes(item)),
  };

  const promises = [];
  if (set.added.length > 0) {
    promises.push(postCDItemsAsync(set.added));
  }
  if (set.updated.length > 0) {
    promises.push(putCDItemsAsync(set.updated));
  }
  if (set.deleted.length > 0) {
    promises.push(deleteCDItemsAsync(set.deleted));
  }
  await axios.all(promises);
  return getCDItemsAsync(ownerId);
};
